class TrVideo extends HTMLElement {
	selectors = {
		video: '[data-tr-js-video]',
		playButton: '[data-tr-js-video-play-button]',
		youtube: '[data-tr-js-youtube]',
		youtubeTemplate: '[data-tr-js-youtube-template]'
	};

	attributes = {
		playLabel: 'data-tr-js-video-play-label',
		pauseLabel: 'data-tr-js-video-pause-label'
	};

	stateClasses = {
		isPlaying: 'is-playing',
		isYoutubeReady: 'is-youtube-ready'
	};

	youtubePlayerStates = {
		ended: 0,
		playing: 1,
		paused: 2
	};

	isYoutubePlaying = false;

	connectedCallback() {
		this.videoElement = this.querySelector(this.selectors.video);
		this.playButtonElement = this.querySelector(this.selectors.playButton);
		this.youtubeTemplateElement = this.querySelector(this.selectors.youtubeTemplate);
		this.youtubeElement = this.querySelector(this.selectors.youtube);

		this.init();
		this.bindEvents();

		document.addEventListener('shopify:section:load', this.onSectionLoad);
	}

	disconnectedCallback() {
		document.removeEventListener('shopify:section:load', this.onSectionLoad);

		this.destroy();
	}

	get isYoutube() {
		return Boolean(this.youtubeTemplateElement || this.youtubeElement);
	}

	init() {
		this.updateState();
	}

	bindEvents() {
		if (!this.playButtonElement) return;

		this.playButtonElement.addEventListener('click', this.onPlayButtonClick);

		if (this.isYoutube) {
			this.playButtonElement.addEventListener('pointerdown', this.onPlayButtonTouch, { passive: true });
			this.playButtonElement.addEventListener('touchstart', this.onPlayButtonTouch, { passive: true });
			window.addEventListener('message', this.onWindowMessage);
			return;
		}

		if (!this.videoElement) return;

		this.videoElement.addEventListener('click', this.onVideoClick);
		this.videoElement.addEventListener('play', this.onVideoPlay);
		this.videoElement.addEventListener('pause', this.onVideoPause);
		this.videoElement.addEventListener('ended', this.onVideoPause);
	}

	onPlayButtonTouch = () => {
		if (this.isYoutube && !this.youtubeElement) {
			this.loadYoutube();
		}
	};

	onSectionLoad = event => {
		if (
			!this.closest(`#${event.detail.sectionId}`) &&
			!this.closest(`#shopify-section-${event.detail.sectionId}`)
		)
			return;

		this.init();
	};

	onPlayButtonClick = () => {
		if (this.isYoutube) {
			this.toggleYoutube();
			return;
		}

		if (!this.videoElement) return;

		if (this.videoElement.paused) {
			this.videoElement.play().catch(() => this.updateState());
			return;
		}

		this.videoElement.pause();
	};

	onVideoPlay = () => {
		this.updateState();
	};

	onVideoClick = () => {
		if (!this.videoElement || this.videoElement.paused) return;

		this.videoElement.pause();
	};

	onVideoPause = () => {
		this.updateState();
	};

	onYoutubeLoad = () => {
		this.classList.add(this.stateClasses.isYoutubeReady);
		this.postYoutubeMessage({ event: 'listening' });
		if (this.isYoutubePlaying) {
			this.postYoutubeMessage({ event: 'command', func: 'playVideo', args: [] });
		}
	};

	onWindowMessage = event => {
		if (!this.youtubeElement || event.source !== this.youtubeElement.contentWindow) return;

		const data = this.parseYoutubeMessage(event.data);

		if (!data) return;

		if (data.event === 'onReady' || data.event === 'initialDelivery') {
			this.classList.add(this.stateClasses.isYoutubeReady);
			if (this.isYoutubePlaying) {
				this.postYoutubeMessage({ event: 'command', func: 'playVideo', args: [] });
			}
		}

		if (data.event === 'onStateChange') {
			this.isYoutubePlaying = data.info === this.youtubePlayerStates.playing;
			this.updateState();
		}
	};

	toggleYoutube() {
		if (this.isYoutubePlaying) {
			this.postYoutubeMessage({ event: 'command', func: 'pauseVideo', args: [] });
			this.isYoutubePlaying = false;
			this.updateState();
			return;
		}

		if (!this.youtubeElement) {
			this.loadYoutube();
			this.isYoutubePlaying = true;
			this.updateState();
			return;
		}

		this.postYoutubeMessage({ event: 'command', func: 'playVideo', args: [] });
		this.isYoutubePlaying = true;
		this.updateState();
	}

	loadYoutube() {
		if (!this.youtubeTemplateElement || !this.playButtonElement || this.youtubeElement) return;

		this.playButtonElement.before(this.youtubeTemplateElement.content.cloneNode(true));
		this.youtubeElement = this.querySelector(this.selectors.youtube);

		if (!this.youtubeElement) return;

		this.youtubeElement.addEventListener('load', this.onYoutubeLoad);
	}

	postYoutubeMessage(payload) {
		if (!this.youtubeElement?.contentWindow) return;

		this.youtubeElement.contentWindow.postMessage(JSON.stringify(payload), '*');
	}

	parseYoutubeMessage(rawData) {
		if (typeof rawData === 'object') return rawData;
		if (typeof rawData !== 'string') return null;

		try {
			return JSON.parse(rawData);
		} catch {
			return null;
		}
	}

	updateState() {
		if (!this.playButtonElement) return;

		const isPlaying = this.getIsPlaying();
		const label = isPlaying
			? this.getAttribute(this.attributes.pauseLabel)
			: this.getAttribute(this.attributes.playLabel);

		this.classList.toggle(this.stateClasses.isPlaying, isPlaying);
		this.playButtonElement.setAttribute('aria-label', label);
	}

	getIsPlaying() {
		if (this.isYoutube) return this.isYoutubePlaying;

		if (!this.videoElement) return false;

		return !this.videoElement.paused && !this.videoElement.ended;
	}

	destroy() {
		this.playButtonElement?.removeEventListener('click', this.onPlayButtonClick);
		this.playButtonElement?.removeEventListener('pointerdown', this.onPlayButtonTouch);
		this.playButtonElement?.removeEventListener('touchstart', this.onPlayButtonTouch);
		window.removeEventListener('message', this.onWindowMessage);
		this.youtubeElement?.removeEventListener('load', this.onYoutubeLoad);

		if (!this.videoElement) return;

		this.videoElement.removeEventListener('click', this.onVideoClick);
		this.videoElement.removeEventListener('play', this.onVideoPlay);
		this.videoElement.removeEventListener('pause', this.onVideoPause);
		this.videoElement.removeEventListener('ended', this.onVideoPause);
	}
}

if (!customElements.get('tr-video')) {
	customElements.define('tr-video', TrVideo);
}
